import type { EffectPreset } from './effectPresets';
import { AUTO_VJ_PACKAGE_ID, type EffectPackageControlValue, type EffectPackageItem } from './effectPackages';
import { silenceFallbackControls } from './silenceFallback';
import {
  applyEffectStackPatch,
  baselineLayer,
  effectStackLayer,
  EFFECT_STACK_DRIVER_SOURCES,
  type EffectStack,
  type EffectStackLayer,
  type EffectStackPatch,
} from './effectStack';

export const AUTO_VJ_LIBRARY_SCHEMA = 'auto-vj-library/1';
const AUTO_VJ_DRIVER_SOURCES = new Set<string>(EFFECT_STACK_DRIVER_SOURCES);

export type AutoVJOrdering = 'deterministic' | 'random';
export type AutoVJTransitionMode = 'cut' | 'crossfade';

export interface AutoVJLookDriver {
  source: string;
  threshold: number;
  amount: number;
  attack: number;
  release: number;
  target: string;
}

export interface AutoVJLookLayer {
  effectId: string;
  zone: 'baseline' | 'reactive';
  parameters: Record<string, EffectPackageControlValue> | null;
  sync: { enabled: boolean; musicRate: string };
  driver?: AutoVJLookDriver;
}

export interface AutoVJLook {
  mix: '' | 'max' | 'add';
  layers: AutoVJLookLayer[] | null;
}

export interface AutoVJStep {
  id: string;
  name: string;
  look: AutoVJLook;
  dwellMinBars: number;
  dwellMaxBars: number;
  transition: { mode: AutoVJTransitionMode; bars: number };
}

export interface AutoVJUnit {
  id: string;
  name: string;
  steps: AutoVJStep[] | null;
}

export interface AutoVJSet {
  id: string;
  name: string;
  ordering: AutoVJOrdering;
  noRepeatWithin: number;
  musicEventSensitivity: number;
  silenceSpeed?: number;
  silenceLevel?: number;
  units: AutoVJUnit[] | null;
}

export interface AutoVJLibrary {
  schema: typeof AUTO_VJ_LIBRARY_SCHEMA;
  activeSetId: string;
  sets: AutoVJSet[] | null;
}

/** The one room-scoped envelope shared by every effect that owns persistent documents. */
export interface EffectDocument<Data = unknown> {
  effectId: string;
  kind: string;
  id: string;
  revision: number;
  updatedAt: string;
  updatedBy: string;
  data: Data;
}

export interface AutoVJActiveData {
  activeSetId: string;
}

export type AutoVJSetDocument = EffectDocument<AutoVJSet> & {
  effectId: typeof AUTO_VJ_PACKAGE_ID;
  kind: 'set';
};

export type AutoVJActiveDocument = EffectDocument<AutoVJActiveData> & {
  effectId: typeof AUTO_VJ_PACKAGE_ID;
  kind: 'active';
  id: 'active';
};

export interface AutoVJStatus {
  schema: 'auto-vj-status/1';
  setId: string;
  setName: string;
  ordering: AutoVJOrdering;
  unitName: string;
  stepName: string;
  stepIndex: number;
  stepCount: number;
  dwellBars: number;
  barsRemaining: number;
  reason: string;
  nextUnitName: string;
  nextStepName: string;
  transitioning: boolean;
  transitionDone: number;
  painted: boolean;
  nonZeroElements: number;
  peak: number;
  layers: Array<{
    id: string;
    effectId: string;
    zone: string;
    version: string;
    state: string;
    envelope: number;
  }> | null;
  clock: {
    mode: string;
    bpm: number;
    confidence: number;
    locked: boolean;
    musicRate: string;
  };
}

function record(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : undefined;
}

function finite(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function parseDriver(value: unknown): AutoVJLookDriver | undefined {
  const raw = record(value);
  if (!raw) return undefined;
  const threshold = finite(raw.threshold);
  const amount = finite(raw.amount);
  const attack = finite(raw.attack);
  const release = finite(raw.release);
  if (typeof raw.source !== 'string' || threshold == null || amount == null
      || attack == null || release == null || typeof raw.target !== 'string') return undefined;
  return { source: raw.source, threshold, amount, attack, release, target: raw.target };
}

function parseLayer(value: unknown): AutoVJLookLayer | undefined {
  const raw = record(value);
  const sync = record(raw?.sync);
  if (!raw || !sync || typeof raw.effectId !== 'string'
      || (raw.zone !== 'baseline' && raw.zone !== 'reactive')
      || typeof sync.enabled !== 'boolean' || typeof sync.musicRate !== 'string' || !sync.musicRate) return undefined;
  const parameters = raw.parameters === null ? null : record(raw.parameters);
  if (parameters === undefined) return undefined;
  const driver = raw.driver == null ? undefined : parseDriver(raw.driver);
  if (raw.driver != null && !driver) return undefined;
  return {
    effectId: raw.effectId,
    zone: raw.zone,
    parameters: parameters as Record<string, EffectPackageControlValue> | null,
    sync: { enabled: sync.enabled, musicRate: sync.musicRate },
    ...(driver ? { driver } : {}),
  };
}

function parseStep(value: unknown): AutoVJStep | undefined {
  const raw = record(value);
  const look = record(raw?.look);
  const transition = record(raw?.transition);
  if (!raw || !look || !transition || typeof raw.id !== 'string' || typeof raw.name !== 'string'
      || (look.mix !== '' && look.mix !== 'max' && look.mix !== 'add')
      || (transition.mode !== 'cut' && transition.mode !== 'crossfade')) return undefined;
  const dwellMinBars = finite(raw.dwellMinBars);
  const dwellMaxBars = finite(raw.dwellMaxBars);
  const bars = finite(transition.bars);
  if (dwellMinBars == null || dwellMaxBars == null || bars == null) return undefined;
  const layers = look.layers === null
    ? null
    : Array.isArray(look.layers)
      ? look.layers.map(parseLayer)
      : undefined;
  if (layers === undefined || layers?.some((layer) => !layer)) return undefined;
  return {
    id: raw.id,
    name: raw.name,
    look: { mix: look.mix, layers: layers as AutoVJLookLayer[] | null },
    dwellMinBars,
    dwellMaxBars,
    transition: { mode: transition.mode, bars },
  };
}

function parseUnit(value: unknown): AutoVJUnit | undefined {
  const raw = record(value);
  if (!raw || typeof raw.id !== 'string' || typeof raw.name !== 'string') return undefined;
  const steps = raw.steps === null
    ? null
    : Array.isArray(raw.steps)
      ? raw.steps.map(parseStep)
      : undefined;
  if (steps === undefined || steps?.some((step) => !step)) return undefined;
  return { id: raw.id, name: raw.name, steps: steps as AutoVJStep[] | null };
}

export function parseAutoVJSet(value: unknown): AutoVJSet | undefined {
  const raw = record(value);
  if (!raw || typeof raw.id !== 'string' || typeof raw.name !== 'string'
      || (raw.ordering !== 'deterministic' && raw.ordering !== 'random')) return undefined;
  const noRepeatWithin = finite(raw.noRepeatWithin);
  const musicEventSensitivity = finite(raw.musicEventSensitivity);
  if (noRepeatWithin == null || musicEventSensitivity == null) return undefined;
  const silenceSpeed = raw.silenceSpeed === undefined ? undefined : finite(raw.silenceSpeed);
  const silenceLevel = raw.silenceLevel === undefined ? undefined : finite(raw.silenceLevel);
  if ((raw.silenceSpeed !== undefined && silenceSpeed === undefined)
      || (raw.silenceLevel !== undefined && silenceLevel === undefined)) return undefined;
  const units = raw.units === null
    ? null
    : Array.isArray(raw.units)
      ? raw.units.map(parseUnit)
      : undefined;
  if (units === undefined || units?.some((unit) => !unit)) return undefined;
  return {
    id: raw.id,
    name: raw.name,
    ordering: raw.ordering,
    noRepeatWithin,
    musicEventSensitivity,
    ...(silenceSpeed === undefined ? {} : { silenceSpeed }),
    ...(silenceLevel === undefined ? {} : { silenceLevel }),
    units: units as AutoVJUnit[] | null,
  };
}

function parsedAutoVJLibrary(value: unknown): AutoVJLibrary | undefined {
  const raw = record(value);
  if (!raw || raw.schema !== AUTO_VJ_LIBRARY_SCHEMA || typeof raw.activeSetId !== 'string') {
    return undefined;
  }
  const sets = raw.sets === null
    ? null
    : Array.isArray(raw.sets)
      ? raw.sets.map(parseAutoVJSet)
      : undefined;
  if (sets === undefined || sets?.some((set) => !set)) return undefined;
  return { schema: AUTO_VJ_LIBRARY_SCHEMA, activeSetId: raw.activeSetId, sets: sets as AutoVJSet[] | null };
}

/** A malformed cloud document costs this screen its library, never its render. */
export function parseAutoVJLibrary(value: unknown): AutoVJLibrary {
  return parsedAutoVJLibrary(value) ?? daemonDefaultAutoVJLibrary();
}

export function parseEffectDocument(value: unknown): EffectDocument | undefined {
  const raw = record(value);
  const revision = finite(raw?.revision);
  if (!raw || typeof raw.effectId !== 'string' || typeof raw.kind !== 'string' || typeof raw.id !== 'string'
      || !Object.prototype.hasOwnProperty.call(raw, 'data')
      || revision == null || !Number.isInteger(revision) || revision < 1
      || typeof raw.updatedAt !== 'string' || typeof raw.updatedBy !== 'string') return undefined;
  return {
    effectId: raw.effectId,
    kind: raw.kind,
    id: raw.id,
    revision,
    updatedAt: raw.updatedAt,
    updatedBy: raw.updatedBy,
    data: raw.data,
  };
}

export function parseAutoVJSetDocument(value: unknown): AutoVJSetDocument | undefined {
  const document = parseEffectDocument(value);
  const set = parseAutoVJSet(document?.data);
  if (!document || document.effectId !== AUTO_VJ_PACKAGE_ID || document.kind !== 'set'
      || !set || set.id !== document.id) return undefined;
  return { ...document, effectId: AUTO_VJ_PACKAGE_ID, kind: 'set', data: set };
}

export function parseAutoVJActiveDocument(value: unknown): AutoVJActiveDocument | undefined {
  const document = parseEffectDocument(value);
  const data = record(document?.data);
  if (!document || document.effectId !== AUTO_VJ_PACKAGE_ID || document.kind !== 'active'
      || document.id !== 'active' || !data || typeof data.activeSetId !== 'string') return undefined;
  return {
    ...document,
    effectId: AUTO_VJ_PACKAGE_ID,
    kind: 'active',
    id: 'active',
    data: { activeSetId: data.activeSetId },
  };
}

export function autoVJSetDocuments(documents: readonly EffectDocument[]): AutoVJSetDocument[] {
  return documents.flatMap((document) => {
    const parsed = parseAutoVJSetDocument(document);
    return parsed ? [parsed] : [];
  }).sort((left, right) => left.id.localeCompare(right.id));
}

export function autoVJActiveDocument(documents: readonly EffectDocument[]): AutoVJActiveDocument | undefined {
  return documents.flatMap((document) => {
    const parsed = parseAutoVJActiveDocument(document);
    return parsed ? [parsed] : [];
  })[0];
}

export function autoVJLibraryFromDocuments(documents: readonly EffectDocument[]): AutoVJLibrary {
  const values = autoVJSetDocuments(documents).map((document) => clone(document.data));
  const activeSetId = autoVJActiveDocument(documents)?.data.activeSetId ?? '';
  return {
    schema: AUTO_VJ_LIBRARY_SCHEMA,
    activeSetId: values.some((set) => set.id === activeSetId) ? activeSetId : '',
    sets: values.length ? values : null,
  };
}

export function daemonDefaultAutoVJLibrary(): AutoVJLibrary {
  return { schema: AUTO_VJ_LIBRARY_SCHEMA, activeSetId: '', sets: null };
}

export function autoVJSets(library: AutoVJLibrary): AutoVJSet[] {
  return library.sets ?? [];
}

/**
 * Structural edits to ONE set — the document the editor saves. Each returns a new set and never
 * mutates its input; the editor reconciles the result into its store by id, so a moved or added unit
 * keeps every other unit's DOM.
 */
export function addAutoVJUnit(set: AutoVJSet, unit: AutoVJUnit): AutoVJSet {
  return { ...clone(set), units: [...(set.units ?? []).map(clone), clone(unit)] };
}

/** The daemon requires fewer "no repeat" slots than units, so removing a unit shrinks the window. */
export function removeAutoVJUnit(set: AutoVJSet, unitId: string): AutoVJSet {
  const units = (set.units ?? []).filter((unit) => unit.id !== unitId).map(clone);
  return {
    ...clone(set),
    noRepeatWithin: Math.min(set.noRepeatWithin, Math.max(0, units.length - 1)),
    units,
  };
}

export function reorderAutoVJUnit(set: AutoVJSet, unitId: string, offset: -1 | 1): AutoVJSet {
  const units = (set.units ?? []).map(clone);
  const from = units.findIndex((unit) => unit.id === unitId);
  const to = from + offset;
  if (from < 0 || to < 0 || to >= units.length) return { ...clone(set), units };
  [units[from], units[to]] = [units[to], units[from]];
  return { ...clone(set), units };
}

function lookFromPreset(preset: EffectPreset): AutoVJLook {
  const layers: AutoVJLookLayer[] = (preset.stack?.layers ?? []).map((layer) => ({
    effectId: layer.effectId,
    zone: layer.zone,
    parameters: clone(layer.parameters),
    sync: clone(layer.sync),
    ...(layer.driver ? { driver: clone(layer.driver) } : {}),
  }));
  return { mix: preset.stack?.mix ?? 'max', layers };
}

export function audioPresetCanBuildStep(preset: EffectPreset): boolean {
  return Boolean(preset.stack?.layers.length);
}

export function copyAudioPresetIntoStep(
  preset: EffectPreset,
  stepId: string,
): AutoVJStep {
  return {
    id: stepId,
    name: preset.name,
    look: lookFromPreset(preset),
    dwellMinBars: 4,
    dwellMaxBars: 4,
    transition: { mode: 'cut', bars: 0 },
  };
}

export function emptyAutoVJStep(id: string, name: string): AutoVJStep {
  return {
    id,
    name,
    look: { mix: 'max', layers: [] },
    dwellMinBars: 4,
    dwellMaxBars: 4,
    transition: { mode: 'cut', bars: 0 },
  };
}

/** Layer ids exist only while the shared stack editor is open; the set document has none. */
export function autoVJLookToStack(look: AutoVJLook, stepId: string): EffectStack {
  return {
    mix: look.mix === 'add' ? 'add' : 'max',
    layers: (look.layers ?? []).map((layer, index) => ({
      id: `${stepId}-layer-${index}`,
      effectId: layer.effectId,
      zone: layer.zone,
      parameters: clone(layer.parameters ?? {}),
      sync: clone(layer.sync),
      ...(layer.driver ? { driver: clone(layer.driver) as EffectStackLayer['driver'] } : {}),
    })),
  };
}

export function autoVJLookFromStack(stack: EffectStack): AutoVJLook {
  return {
    mix: stack.mix,
    layers: stack.layers.map((layer) => ({
      effectId: layer.effectId,
      zone: layer.zone,
      parameters: clone(layer.parameters),
      sync: clone(layer.sync),
      ...(layer.driver ? { driver: clone(layer.driver) } : {}),
    })),
  };
}

/** Apply the audio card's exact edit operation to a step's private working stack. */
export function composeAutoVJStack(
  stack: EffectStack,
  patch: EffectStackPatch,
  items: readonly EffectPackageItem[],
  newLayerId: string,
): EffectStack {
  const effectId = 'add' in patch ? patch.add.effectId
    : 'baseline' in patch ? patch.baseline?.effectId
      : 'layer' in patch ? patch.layer.effectId : undefined;
  const item = effectId ? items.find((candidate) => candidate.packageId === effectId) : undefined;
  const zone = 'add' in patch ? 'reactive'
    : 'baseline' in patch ? 'baseline'
      : 'layer' in patch ? stack.layers.find((layer) => layer.id === patch.layer.id)?.zone : undefined;
  const id = 'baseline' in patch ? baselineLayer(stack.layers)?.id ?? newLayerId
    : 'layer' in patch ? patch.layer.id : newLayerId;
  const opened = item && zone ? effectStackLayer(item, zone, id) : undefined;
  if (effectId && !opened) throw new Error(`auto vj: ${effectId} is not in the room's layer catalog`);
  return applyEffectStackPatch(stack, patch, opened);
}

function replaceUnit(set: AutoVJSet, unitId: string, update: (unit: AutoVJUnit) => AutoVJUnit): AutoVJSet {
  return {
    ...clone(set),
    units: (set.units ?? []).map((unit) => unit.id === unitId ? update(clone(unit)) : clone(unit)),
  };
}

export function addAutoVJStep(set: AutoVJSet, unitId: string, step: AutoVJStep): AutoVJSet {
  return replaceUnit(set, unitId, (unit) => ({ ...unit, steps: [...(unit.steps ?? []), clone(step)] }));
}

export function removeAutoVJStep(set: AutoVJSet, unitId: string, stepId: string): AutoVJSet {
  return replaceUnit(set, unitId, (unit) => ({ ...unit, steps: (unit.steps ?? []).filter((step) => step.id !== stepId) }));
}

function validName(value: string, field: string): string | undefined {
  if (!value.trim()) return `auto vj: ${field} must not be empty`;
  if ([...value].length > 64) return `auto vj: ${field} must be at most 64 characters`;
  if (/[\n\r\t]/.test(value)) return `auto vj: ${field} must not contain line breaks or tabs`;
  return undefined;
}

function validateAutoVJSet(set: AutoVJSet, prefix: string): string | undefined {
  const nameError = validName(set.id, `${prefix}.id`) ?? validName(set.name, `${prefix}.name`);
  if (nameError) return nameError;
  if (set.ordering !== 'deterministic' && set.ordering !== 'random') return `auto vj: ${prefix}.ordering is not deterministic or random`;
  const units = set.units ?? [];
  if (units.length > 64) return `auto vj: ${prefix} has ${units.length} units, more than the 64 a set may hold`;
  if (!Number.isInteger(set.noRepeatWithin) || set.noRepeatWithin < 0 || set.noRepeatWithin > 63) return `auto vj: ${prefix}.noRepeatWithin must be between 0 and 63`;
  if (set.ordering === 'random' && units.length > 0 && set.noRepeatWithin >= units.length) return `auto vj: ${prefix}.noRepeatWithin must be smaller than the unit count`;
  if (!Number.isFinite(set.musicEventSensitivity) || set.musicEventSensitivity < 0 || set.musicEventSensitivity > 1) return `auto vj: ${prefix}.musicEventSensitivity must be between 0 and 1`;
  for (const key of ['silenceSpeed', 'silenceLevel'] as const) {
    const value = set[key];
    if (value !== undefined && (!Number.isFinite(value) || value < 0 || value > 1)) {
      return `auto vj: ${prefix}.${key} must be between 0 and 1`;
    }
  }
  const unitIds = new Set<string>();
  for (let unitIndex = 0; unitIndex < units.length; unitIndex += 1) {
    const unit = units[unitIndex];
    const unitPrefix = `${prefix}.units[${unitIndex}]`;
    const unitNameError = validName(unit.id, `${unitPrefix}.id`) ?? validName(unit.name, `${unitPrefix}.name`);
    if (unitNameError) return unitNameError;
    if (unitIds.has(unit.id)) return `auto vj: ${prefix} has two units sharing the id ${JSON.stringify(unit.id)}`;
    unitIds.add(unit.id);
    const steps = unit.steps ?? [];
    if (steps.length > 16) return `auto vj: ${unitPrefix} has ${steps.length} steps, more than the 16 a unit may hold`;
    const stepIds = new Set<string>();
    for (let stepIndex = 0; stepIndex < steps.length; stepIndex += 1) {
      const step = steps[stepIndex];
      const stepPrefix = `${unitPrefix}.steps[${stepIndex}]`;
      const stepNameError = validName(step.id, `${stepPrefix}.id`) ?? validName(step.name, `${stepPrefix}.name`);
      if (stepNameError) return stepNameError;
      if (stepIds.has(step.id)) return `auto vj: ${unitPrefix} has two steps sharing the id ${JSON.stringify(step.id)}`;
      stepIds.add(step.id);
      if (!Number.isInteger(step.dwellMinBars) || step.dwellMinBars < 1) return `auto vj: ${stepPrefix}.dwellMinBars must be at least 1 bar`;
      if (!Number.isInteger(step.dwellMaxBars) || step.dwellMaxBars < step.dwellMinBars) return `auto vj: ${stepPrefix}.dwellMaxBars must be at least dwellMinBars`;
      if (step.dwellMaxBars > 256) return `auto vj: ${stepPrefix}.dwellMaxBars must be at most 256 bars`;
      if (step.transition.mode === 'cut' && step.transition.bars !== 0) return `auto vj: ${stepPrefix}.transition.bars must be 0 for a cut`;
      if (step.transition.mode === 'crossfade'
          && (!Number.isInteger(step.transition.bars) || step.transition.bars < 1 || step.transition.bars > 32 || step.transition.bars > step.dwellMinBars)) {
        return `auto vj: ${stepPrefix}.transition.bars must be between 1 and dwellMinBars`;
      }
      if (step.transition.mode !== 'cut' && step.transition.mode !== 'crossfade') return `auto vj: ${stepPrefix}.transition.mode is not cut or crossfade`;
      if (step.look.mix !== '' && step.look.mix !== 'add' && step.look.mix !== 'max') return `auto vj: ${stepPrefix}.look.mix is not add or max`;
      const layers = step.look.layers ?? [];
      if (layers.length > 16) return `auto vj: ${stepPrefix}.look has more than 16 layers`;
      if (layers.filter((layer) => layer.zone === 'baseline').length > 1) return `auto vj: ${stepPrefix}.look has more than one baseline layer`;
      for (let layerIndex = 0; layerIndex < layers.length; layerIndex += 1) {
        const layer = layers[layerIndex];
        const layerPrefix = `${stepPrefix}.look.layers[${layerIndex}]`;
        if (!layer.effectId || layer.effectId.trim() !== layer.effectId || layer.effectId.length > 96) return `auto vj: ${layerPrefix}.effectId must be a package id`;
        if (layer.zone !== 'baseline' && layer.zone !== 'reactive') return `auto vj: ${layerPrefix}.zone is not baseline or reactive`;
        if (typeof layer.sync.musicRate !== 'string' || !layer.sync.musicRate) return `auto vj: ${layerPrefix}.sync.musicRate is not an effect rate`;
        if (layer.zone === 'baseline' && layerIndex !== 0) return `auto vj: ${layerPrefix} is the baseline, so it must be the first layer`;
        if (layer.zone === 'baseline' && layer.driver) return `auto vj: ${layerPrefix} is the baseline, so it must carry no driver`;
        if (layer.zone === 'reactive' && !layer.driver) return `auto vj: ${layerPrefix} is reactive, so it must carry a driver`;
        if (!layer.driver) continue;
        const driver = layer.driver;
        if (!AUTO_VJ_DRIVER_SOURCES.has(driver.source)) return `auto vj: ${layerPrefix}.driver.source is not a music feature`;
        if (!Number.isFinite(driver.threshold) || driver.threshold < 0 || driver.threshold > 1) return `auto vj: ${layerPrefix}.driver.threshold must be between 0 and 1`;
        if (!Number.isFinite(driver.amount) || driver.amount < -1 || driver.amount > 1) return `auto vj: ${layerPrefix}.driver.amount must be between -1 and 1`;
        if (!Number.isFinite(driver.attack) || driver.attack < 0 || driver.attack > 30) return `auto vj: ${layerPrefix}.driver.attack must be between 0 and 30 seconds`;
        if (!Number.isFinite(driver.release) || driver.release < 0 || driver.release > 30) return `auto vj: ${layerPrefix}.driver.release must be between 0 and 30 seconds`;
        if (!driver.target.trim()) return `auto vj: ${layerPrefix}.driver.target must name what the driver moves`;
      }
    }
  }
  return undefined;
}

/** The client-side half of the daemon boundary; the daemon still validates and owns every refusal. */
export function validateAutoVJSetBeforeSend(set: AutoVJSet): string | undefined {
  return validateAutoVJSet(set, 'set');
}

// ---- The Auto VJ tree's own parameters (S34) ---------------------------------------------------------
//
// A set's and a step's settings are drawn by the SAME parameter row a flat card uses, so they are
// written here in the spec a manifest uses. The words are the ones the console already showed for them.

function autoVJCard(name: string, parameters: EffectPackageItem['parameters']): EffectPackageItem {
  return {
    packageId: AUTO_VJ_PACKAGE_ID,
    name,
    description: '',
    version: '',
    origin: 'nuance',
    available: true,
    unavailableReason: '',
    state: '',
    stateLabel: '',
    parameters,
    parameterOrder: Object.keys(parameters),
    audio: 'none',
    sync: {},
  };
}

/** A set's settings: Order, No repeat within (Random only, fewer than the units) and Music sensitivity. */
export function autoVJSetParameters(unitCount: number, card?: EffectPackageItem): EffectPackageItem {
  return autoVJCard('Set', {
    ordering: {
      type: 'enum', default: 'deterministic', values: ['deterministic', 'random'],
      label: 'Order', labels: ['In order', 'Random'],
    },
    noRepeatWithin: {
      type: 'integer', default: 0, min: 0, max: Math.max(0, unitCount - 1), step: 1,
      label: 'No repeat within', visibleWhen: [{ parameter: 'ordering', in: ['random'] }],
    },
    musicEventSensitivity: { type: 'number', default: 0.5, min: 0, max: 1, step: 0.05, label: 'Music sensitivity' },
    ...Object.fromEntries(silenceFallbackControls(card).map(({ key, label, spec }) => [
      key, { ...spec, label },
    ])),
  });
}

export function autoVJSetValues(set: AutoVJSet, card?: EffectPackageItem): Record<string, EffectPackageControlValue> {
  return {
    ordering: set.ordering,
    noRepeatWithin: set.noRepeatWithin,
    musicEventSensitivity: set.musicEventSensitivity,
    ...Object.fromEntries(silenceFallbackControls(card).map(({ key, spec }) => [key, set[key] ?? spec.default])),
  };
}

/**
 * A step's settings: Mix, Dwell (one min / max pair, in bars), Transition and its Bars (Crossfade only,
 * never past the shortest dwell).
 */
export function autoVJStepParameters(dwellMinBars: number): EffectPackageItem {
  return autoVJCard('Step', {
    mix: { type: 'enum', default: 'max', values: ['max', 'add'], label: 'Mix', labels: ['Brightest wins', 'Add'] },
    minDwellBars: { type: 'integer', default: 4, min: 1, max: 256, step: 1, label: 'Dwell min' },
    maxDwellBars: { type: 'integer', default: 4, min: 1, max: 256, step: 1, label: 'Dwell max' },
    transition: {
      type: 'enum', default: 'cut', values: ['cut', 'crossfade'], label: 'Transition', labels: ['Cut', 'Crossfade'],
    },
    transitionBars: {
      type: 'integer', default: 1, min: 1, max: Math.max(1, Math.min(32, dwellMinBars)), step: 1,
      label: 'Bars', visibleWhen: [{ parameter: 'transition', in: ['crossfade'] }],
    },
  });
}

export function autoVJStepValues(step: AutoVJStep): Record<string, EffectPackageControlValue> {
  return {
    mix: step.look.mix === 'add' ? 'add' : 'max',
    minDwellBars: step.dwellMinBars,
    maxDwellBars: step.dwellMaxBars,
    transition: step.transition.mode,
    transitionBars: step.transition.mode === 'crossfade' ? step.transition.bars : 1,
  };
}

/**
 * What one parameter-row gesture changes on a step. Mix belongs to the look, which the caller edits
 * through the stack; the rest are the step's schedule. Choosing Crossfade starts at one bar and Cut
 * carries none, as the daemon requires.
 */
export function autoVJStepChange(
  step: AutoVJStep,
  values: Record<string, EffectPackageControlValue>,
): { mix?: 'max' | 'add'; step: Partial<Pick<AutoVJStep, 'dwellMinBars' | 'dwellMaxBars' | 'transition'>> } {
  const change: ReturnType<typeof autoVJStepChange> = { step: {} };
  if (values.mix === 'max' || values.mix === 'add') change.mix = values.mix;
  if (typeof values.minDwellBars === 'number' && Number.isFinite(values.minDwellBars)) change.step.dwellMinBars = values.minDwellBars;
  if (typeof values.maxDwellBars === 'number' && Number.isFinite(values.maxDwellBars)) change.step.dwellMaxBars = values.maxDwellBars;
  if (values.transition === 'cut') change.step.transition = { mode: 'cut', bars: 0 };
  else if (values.transition === 'crossfade') {
    change.step.transition = { mode: 'crossfade', bars: step.transition.mode === 'crossfade' ? step.transition.bars : 1 };
  }
  if (typeof values.transitionBars === 'number' && Number.isFinite(values.transitionBars) && step.transition.mode === 'crossfade') {
    change.step.transition = { mode: 'crossfade', bars: values.transitionBars };
  }
  return change;
}

/**
 * A collapsed step's one line, built only from values the step already holds: its first layer's name,
 * its dwell in bars, and its transition — `Candlelight · 8–16 bars · Cut`.
 */
export function autoVJStepSummary(step: AutoVJStep, layerName: (effectId: string) => string): string {
  const first = (step.look.layers ?? [])[0];
  const dwell = step.dwellMinBars === step.dwellMaxBars
    ? `${step.dwellMinBars} ${step.dwellMinBars === 1 ? 'bar' : 'bars'}`
    : `${step.dwellMinBars}–${step.dwellMaxBars} bars`;
  const transition = step.transition.mode === 'crossfade' ? 'Crossfade' : 'Cut';
  return [...(first ? [layerName(first.effectId)] : []), dwell, transition].join(' · ');
}
